import { useState, useCallback, useRef } from 'react';
import { UseMutationResult } from '@tanstack/react-query';

interface ResearchGenerationTask {
  id: 'preparing' | 'searching' | 'analyzing' | 'generating' | 'formatting' | 'saving';
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
}

interface ResearchGenerationState {
  status: 'idle' | 'processing' | 'success' | 'error';
  progress: number;
  tasks: ResearchGenerationTask[];
  currentTaskDescription: string;
  error: string | null;
  generatedResearchId: string | null;
}

const initialState: ResearchGenerationState = {
  status: 'idle',
  progress: 0,
  tasks: [
    { id: 'preparing', name: 'Preparing Query', status: 'pending' },
    { id: 'searching', name: 'Searching Sources', status: 'pending' },
    { id: 'generating', name: 'Creating Smart Title', status: 'pending' },
    { id: 'analyzing', name: 'Processing Data', status: 'pending' },
    { id: 'saving', name: 'Saving to Library', status: 'pending' },
    { id: 'formatting', name: 'Finalizing Research', status: 'pending' },
  ],
  currentTaskDescription: '',
  error: null,
  generatedResearchId: null,
};

export const useResearchGenerationProgress = (
  researchMutation: UseMutationResult<any, Error, any, unknown>
) => {
  const [state, setState] = useState<ResearchGenerationState>(initialState);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  const updateTaskStatus = useCallback((taskId: string, status: 'pending' | 'in-progress' | 'completed' | 'failed') => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === taskId ? { ...task, status } : task
      )
    }));
  }, []);

  const simulateProgress = useCallback((fromProgress: number, toProgress: number, duration: number) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    const startProgress = fromProgress;
    const progressRange = toProgress - fromProgress;
    const startTime = Date.now();

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercent = Math.min(elapsed / duration, 1);
      
      // Use easing function for more natural progress
      const easedProgress = progressPercent < 0.5 
        ? 2 * progressPercent * progressPercent 
        : 1 - Math.pow(-2 * progressPercent + 2, 2) / 2;
      
      const currentProgress = startProgress + (progressRange * easedProgress);
      
      setState(prev => ({ ...prev, progress: Math.round(currentProgress) }));

      if (progressPercent >= 1) {
        clearInterval(progressIntervalRef.current!);
        progressIntervalRef.current = null;
      }
    }, 100);
  }, []);

  const startGeneration = useCallback(async (params: any) => {
    setState(prev => ({ ...prev, status: 'processing', error: null }));
    startTimeRef.current = Date.now();

    try {
      // Step 1: Preparing Query (0-15%) - Based on console: "Starting private research generation" → "Using research type"
      setState(prev => ({ 
        ...prev, 
        progress: 0, 
        currentTaskDescription: 'Preparing your research query and parameters...' 
      }));
      updateTaskStatus('preparing', 'in-progress');
      simulateProgress(0, 15, 800);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      updateTaskStatus('preparing', 'completed');

      // Step 2: Searching Sources (15-70%) - This is the LONGEST step
      // Based on console: The gap between "Using research type" and "Private research data received"
      setState(prev => ({ 
        ...prev, 
        currentTaskDescription: 'Searching academic sources and gathering information...' 
      }));
      updateTaskStatus('searching', 'in-progress');
      
      // Start progress simulation for the long API call
      simulateProgress(15, 65, 14000); // ~14 seconds based on console timing

      // Execute the actual research generation - this is where the long wait happens
      const result = await researchMutation.mutateAsync(params);
      
      // Stop progress simulation and set to 70%
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setState(prev => ({ ...prev, progress: 70 }));
      updateTaskStatus('searching', 'completed');

      // Step 3: Generating Title (70-85%) - Based on console: "Generating header" → "Generated header"
      setState(prev => ({ 
        ...prev, 
        currentTaskDescription: 'Creating an intelligent title for your research...' 
      }));
      updateTaskStatus('generating', 'in-progress');
      simulateProgress(70, 85, 2500); // ~2-3 seconds based on console
      await new Promise(resolve => setTimeout(resolve, 2500));
      updateTaskStatus('generating', 'completed');

      // Step 4: Processing Data (85-95%) - Based on console: "Private research parameters object"
      setState(prev => ({ 
        ...prev, 
        currentTaskDescription: 'Processing data and metadata...' 
      }));
      updateTaskStatus('analyzing', 'in-progress');
      simulateProgress(85, 95, 500);
      await new Promise(resolve => setTimeout(resolve, 500));
      updateTaskStatus('analyzing', 'completed');

      // Step 5: Saving (95-100%) - Based on console: "Private research saved successfully"
      setState(prev => ({ 
        ...prev, 
        currentTaskDescription: 'Saving research to your library...' 
      }));
      updateTaskStatus('saving', 'in-progress');
      simulateProgress(95, 100, 300);
      await new Promise(resolve => setTimeout(resolve, 300));
      updateTaskStatus('saving', 'completed');

      // Step 6: Finalizing - Based on console: "Private research generation successful"
      setState(prev => ({ 
        ...prev, 
        currentTaskDescription: 'Finalizing and preparing research...' 
      }));
      updateTaskStatus('formatting', 'in-progress');
      await new Promise(resolve => setTimeout(resolve, 200));
      updateTaskStatus('formatting', 'completed');

      // Success
      const totalTime = Date.now() - startTimeRef.current;
      console.log(`Research generation completed in ${totalTime}ms`);

      setState(prev => ({ 
        ...prev, 
        status: 'success',
        progress: 100,
        currentTaskDescription: 'Research created successfully!',
        generatedResearchId: result?.id || null
      }));

      return result;

    } catch (error: any) {
      console.error('[ResearchGenerationProgress] Error:', error);
      
      // Clear any running progress simulation
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      // Find which task failed based on progress
      const failedTaskId = state.progress < 70 ? 'searching' : 
                          state.progress < 85 ? 'generating' :
                          state.progress < 95 ? 'analyzing' :
                          state.progress < 100 ? 'saving' : 'formatting';
      
      updateTaskStatus(failedTaskId, 'failed');

      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to generate research. Please try again.',
        currentTaskDescription: 'An error occurred during research generation.'
      }));

      throw error;
    }
  }, [researchMutation, updateTaskStatus, simulateProgress, state.progress]);

  const reset = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setState(initialState);
  }, []);

  return {
    state,
    startGeneration,
    reset
  };
};